import EastRoundedIcon from "@mui/icons-material/EastRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { Box, Collapse, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FONT_SIZE, INK, LETTER_SPACING, MOTION, OUTREACH_POPUP, RADIUS } from "../../../theme/tokens";
import { OutreachStatus, type RequestOutreach } from "../../../utils/models";
import { SystemLevelLadder } from "../../UIToolkit/DataViz/SystemLevelLadder/SystemLevelLadder";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { OutreachPaceWhy } from "./OutreachPaceWhy";
import { OutreachStatusChip } from "./OutreachStatusChip";
import { effortLadderOf } from "./outreachEffortView";
import type { OutreachCopy } from "./useOutreachCopy";

type Props = {
	outreach: RequestOutreach;
	copy: OutreachCopy;
	now: DateTime;
	panelOpen: boolean;
	onTogglePause: () => void;
	onOpenPanel: () => void;
};

const chevronSx = (open: boolean) => ({
	transform: open ? "rotate(180deg)" : "none",
	transition: `transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

/** How hard outreach is pushing for this shift: where it stands, the level ladder, why it paces as it does, and the controls on it. */
export const OutreachEffortPanel: FC<Props> = ({ outreach, copy, now, panelOpen, onTogglePause, onOpenPanel }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const titleId = useId();
	const [whyOpen, setWhyOpen] = useState(false);
	const paused = outreach.status === OutreachStatus.Paused;
	const running = outreach.status === OutreachStatus.Running;
	const ladder = effortLadderOf(t, copy, outreach, now, paused);

	return (
		<Box
			component="section"
			aria-labelledby={titleId}
			data-testid="outreach-effort"
			sx={(theme) => ({
				display: "flex",
				flexDirection: "column",
				gap: 1,
				px: 2,
				pt: 1.5,
				pb: 1.75,
				borderRadius: RADIUS.CONTROL / RADIUS.CARD,
				backgroundColor: alpha(theme.palette.primary.main, OUTREACH_POPUP.EFFORT_WASH_ALPHA),
			})}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
				<Typography id={titleId} component="h2" sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 700, color: INK.PRIMARY }}>
					{t("request.outreach.effort.title")}
				</Typography>
				<SystemButton
					data-testid="outreach-why-pace"
					buttonVariant="secondary"
					size="compact"
					noFullWidthMobile
					aria-expanded={whyOpen}
					onClick={() => {
						if (!whyOpen) track("outreach_pace_why_opened", { request_id: outreach.requestId, level: outreach.effort.level });
						setWhyOpen((open) => !open);
					}}
				>
					{t("request.outreach.effort.why")}
					<ExpandMoreRoundedIcon fontSize="small" sx={chevronSx(whyOpen)} />
				</SystemButton>
				<Box sx={{ flex: 1 }} />
				<OutreachStatusChip status={outreach.status} />
				<SystemButton data-testid="outreach-pause-toggle" buttonVariant="secondary" noFullWidthMobile disabled={!running && !paused} onClick={onTogglePause}>
					{t(paused ? "request.outreach.action.resume" : "request.outreach.action.pause")}
				</SystemButton>
				<SystemButton
					data-testid="outreach-adjust-plan"
					buttonVariant="primary"
					noFullWidthMobile
					leadingIcon={<TuneRoundedIcon fontSize="small" />}
					aria-expanded={panelOpen}
					onClick={onOpenPanel}
				>
					{t("request.outreach.effort.adjust")}
				</SystemButton>
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 0.5 }}>
				<Typography component="span" data-testid="outreach-level-title" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.SECONDARY }}>
					{ladder.title}
				</Typography>
				<Box
					component="span"
					aria-hidden
					sx={{
						display: "inline-flex",
						alignItems: "center",
						gap: 0.75,
						fontSize: FONT_SIZE.LABEL_XS,
						fontWeight: 700,
						letterSpacing: LETTER_SPACING.LABEL,
						textTransform: "uppercase",
						color: "primary.main",
					}}
				>
					{t("request.outreach.effort.axis")}
					<EastRoundedIcon fontSize="inherit" />
				</Box>
			</Box>
			<SystemLevelLadder
				levels={ladder.levels}
				progress={ladder.progress}
				markerLabel={ladder.markerLabel}
				ariaLabel={t("request.outreach.effort.aria")}
				dataTestId="outreach-levels"
			/>
			<Collapse in={whyOpen} unmountOnExit>
				<OutreachPaceWhy outreach={outreach} copy={copy} now={now} />
			</Collapse>
		</Box>
	);
};
